import agricarbonLogoWhite from '@/assets/images/agricarbon-logo-white.png';
import { Body2 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { usePermissions, UserPermission, UserPermissions } from '@/modules/Permissions';
import { routes } from '@/modules/Router';
import { HandleLogoutWrapper } from '@/utils/logout';
import { FileText, LogOut, LucideIcon, Tractor, UserRound } from 'lucide-react';
import { Link, useFetcher, useLocation } from 'react-router-dom';

type RouteKeys = keyof typeof routes;
type RouteValues = (typeof routes)[RouteKeys];

type SideBarMenuLinkProps = {
  icon: LucideIcon;
  title: string;
  linkPage: RouteValues;
  permission?: UserPermission;
};

export const SideBarMenuLink = ({ icon: Icon, title, linkPage, permission }: SideBarMenuLinkProps) => {
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // Determine if the current route matches the button's route
  const isActive = location.pathname === linkPage;

  return (
    <>
      {(!permission || hasPermission(permission)) && (
        <Link to={linkPage}>
          <div
            className={cn(`h-10 w-full flex flex-row gap-2 items-center rounded-lg p-2`, isActive ? 'bg-brand-50' : 'hover:bg-brand-50')}
          >
            <Icon />
            <Body2 className="text-neutral-0">{title}</Body2>
          </div>
        </Link>
      )}
    </>
  );
};

type SideBarMenuButtonProps = {
  icon: LucideIcon;
  title: string;
};

export const SideBarMenuButton = ({ icon: Icon, title }: SideBarMenuButtonProps) => {
  const fetcher = useFetcher();
  const isLoggingOut = fetcher.formData != null;
  return (
    <button
      type="submit"
      disabled={isLoggingOut}
      className={cn('h-10 w-full flex flex-row gap-2 items-center rounded-lg p-2 hover:bg-brand-50')}
    >
      <Icon />
      <Body2 className="text-neutral-0">{isLoggingOut ? 'Signing out...' : title}</Body2>
    </button>
  );
};

export const UserBasedSideBarMenu = () => {
  return (
    <div className={cn('flex flex-col gap-2')}>
      <img src={agricarbonLogoWhite} alt="Agricarbon Logo" className={cn('h-12 object-contain mb-8')} />
      <SideBarMenuLink icon={UserRound} title="Customers" linkPage={routes.customers} permission={UserPermissions.CUSTOMER_LIST} />
      <SideBarMenuLink icon={FileText} title="Projects" linkPage={routes.projects} permission={UserPermissions.PROJECT_LIST} />
      <SideBarMenuLink icon={Tractor} title="Farms" linkPage={routes.farms} permission={UserPermissions.FARM_LIST} />
      <SideBarMenuLink icon={UserRound} title="Users" linkPage={routes.users} permission={UserPermissions.USER_LIST} />
    </div>
  );
};

export const StandardSideBarMenu = () => {
  return (
    <>
      <SideBarMenuLink icon={UserRound} title="Profile" linkPage={routes.profile} />
      <HandleLogoutWrapper>
        <SideBarMenuButton icon={LogOut} title="Logout" />
      </HandleLogoutWrapper>
    </>
  );
};
